import { useI18n, type MessageKey } from '@ecom/i18n';
import { delegationsOf } from '@ecom/i18n/delegations';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Field, GovernorateSelect, Input, Modal, Select } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  PARCEL_TYPES,
  initialValues,
  type RequiredField,
  type SendFieldError,
  type SendValues,
  useSendFormQuery,
  useSendToJibexMutation,
  validateSendValues,
} from '../send-to-jibex';

import { lastErrorKey } from '@/features/delivery/shipments/components/ShipmentBadges';
import { formatCatalogPrice } from '@/features/products/format-price';

/** How long the screen follows a send before saying it continues in the background. */
const FOLLOW_FOR_MS = 45_000;

const ERROR_KEY: Record<SendFieldError, MessageKey> = {
  required: 'merchant.sendJibex.required',
  delegationMismatch: 'merchant.sendJibex.delegationMismatch',
  phoneInvalid: 'merchant.sendJibex.phoneInvalid',
  phone2Invalid: 'merchant.sendJibex.phone2Invalid',
  weightInvalid: 'merchant.sendJibex.weightInvalid',
  piecesInvalid: 'merchant.sendJibex.piecesInvalid',
};

type Phase = 'edit' | 'following' | 'done';

/**
 * « Confirmer et envoyer à Jibex » — the order reviewed as Jibex's « Nouveau colis » screen asks for it (Destinataire ·
 * Informations du colis), pre-filled from Souk. Fields the order lacks are flagged; nothing is sent until every field
 * is valid; the send button is disabled while it runs (a double click cannot create two parcels — the API refuses the
 * second send anyway). After sending, the screen follows the parcel until Jibex created it (tracking number) or refused
 * it (its reason, the form kept as typed so the merchant only corrects).
 *
 * No Jibex account: the order is only marked « Préparée », as before (`onMarkPacked`).
 */
export function SendToJibexModal({
  orderId,
  onClose,
  onMarkPacked,
  markingPacked,
}: {
  orderId: string | null;
  onClose: () => void;
  onMarkPacked: () => void;
  markingPacked: boolean;
}): ReactElement {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>('edit');
  const [followSince, setFollowSince] = useState<number | null>(null);
  const [values, setValues] = useState<SendValues | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof SendValues, SendFieldError>>>({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const form = useSendFormQuery(orderId, phase === 'following');
  const send = useSendToJibexMutation();

  // A new order: start from its own pre-filled values.
  useEffect(() => {
    setPhase('edit');
    setValues(null);
    setErrors({});
    setRequestError(null);
    setTimedOut(false);
    setFollowSince(null);
  }, [orderId]);

  // First load (or a reload after the merchant fixed something elsewhere): pre-fill, never overwrite what is typed.
  useEffect(() => {
    if (form.data !== undefined && values === null) setValues(initialValues(form.data));
  }, [form.data, values]);

  // Following the send: stop on the outcome, or after FOLLOW_FOR_MS (it continues in the background).
  const shipment = form.data?.shipment ?? null;
  useEffect(() => {
    if (phase !== 'following') return undefined;
    if ((shipment?.trackingNumber ?? null) !== null) {
      setPhase('done');
      return undefined;
    }
    if (shipment?.status === 'dispatch_failed') {
      setPhase('edit');
      return undefined;
    }
    const left = FOLLOW_FOR_MS - (Date.now() - (followSince ?? Date.now()));
    const timer = setTimeout(
      () => {
        setTimedOut(true);
        setPhase('done');
      },
      Math.max(0, left),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [phase, shipment, followSince]);

  function submit(): void {
    if (orderId === null || values === null || send.isPending) return;
    setRequestError(null);
    const checked = validateSendValues(values);
    if ('errors' in checked) {
      setErrors(checked.errors);
      return;
    }
    setErrors({});
    send.mutate(
      { orderId, body: checked.body },
      {
        onSuccess: () => {
          setTimedOut(false);
          setFollowSince(Date.now());
          setPhase('following');
        },
        onError: (error) => {
          // The typed values stay in the form: the merchant only corrects and sends again.
          setRequestError(t(resolveErrorMessageKey(error)));
        },
      },
    );
  }

  const data = form.data;
  const missing = new Set<RequiredField>(data?.missing ?? []);
  const trackingNumber = shipment?.trackingNumber ?? null;
  const alreadyAtCarrier = trackingNumber !== null;
  const failure =
    phase === 'edit' && shipment?.status === 'dispatch_failed' && shipment.lastError !== null
      ? (() => {
          const key = lastErrorKey(shipment.lastError);
          return key === undefined
            ? t('merchant.ship.lastError.other', { code: shipment.lastError })
            : t(key);
        })()
      : null;
  const showForm =
    data !== undefined &&
    values !== null &&
    data.carrierConnected &&
    data.sendable &&
    !alreadyAtCarrier &&
    phase === 'edit';
  const busy = send.isPending || phase === 'following';

  const set = <K extends keyof SendValues>(key: K, value: SendValues[K]): void => {
    setValues((current) => (current === null ? current : { ...current, [key]: value }));
  };
  const fieldError = (key: keyof SendValues): string | undefined => {
    const error = errors[key];
    if (error !== undefined) return t(ERROR_KEY[error]);
    return undefined;
  };
  const missingHint = (field: RequiredField): string | undefined =>
    missing.has(field) && errors[field] === undefined
      ? t('merchant.sendJibex.missingField')
      : undefined;

  // The order's own delegation text when it matched no listed name: shown so the merchant picks the right one.
  const typedDelegation = data?.values.delegation.trim() ?? '';
  const typedDelegationHint =
    values !== null && values.delegation === '' && typedDelegation !== ''
      ? t('merchant.sendJibex.typedDelegation', { value: typedDelegation })
      : undefined;

  let body: ReactNode;
  if (form.isPending || (data !== undefined && values === null)) {
    body = <p className="text-sm text-inkSoft">{t('merchant.common.loadingAria')}</p>;
  } else if (form.isError || data === undefined || values === null) {
    body = (
      <p role="alert" className="text-sm text-critical">
        {form.error !== null ? t(resolveErrorMessageKey(form.error)) : t('errors.server')}
      </p>
    );
  } else if (!data.carrierConnected) {
    body = <p className="text-sm text-ink">{t('merchant.sendJibex.noCarrier')}</p>;
  } else if (alreadyAtCarrier) {
    body = (
      <p role="status" className="rounded-md bg-goodSoft px-4 py-3 text-sm text-good">
        {phase === 'done'
          ? t('merchant.sendJibex.success', { tracking: trackingNumber })
          : t('merchant.sendJibex.alreadySent', { tracking: trackingNumber })}{' '}
        <Link to="/delivery" className="font-medium underline">
          {t('merchant.sendJibex.openDelivery')}
        </Link>
      </p>
    );
  } else if (phase === 'following' || (phase === 'done' && timedOut)) {
    body = (
      <p role="status" className="rounded-md bg-infoSoft px-4 py-3 text-sm text-ink">
        {timedOut ? t('merchant.sendJibex.stillSending') : t('merchant.sendJibex.sending')}{' '}
        {timedOut ? (
          <Link to="/delivery" className="font-medium underline">
            {t('merchant.sendJibex.openDelivery')}
          </Link>
        ) : null}
      </p>
    );
  } else if (!data.sendable) {
    body = (
      <p className="text-sm text-ink">
        {shipment !== null && shipment.status !== 'dispatch_failed'
          ? t('merchant.sendJibex.inProgress')
          : t('merchant.sendJibex.notSendable')}
      </p>
    );
  } else {
    body = (
      <form
        id="send-to-jibex-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="flex flex-col gap-4"
      >
        <p className="text-sm text-inkSoft">
          {t('merchant.sendJibex.orderLine', {
            number: data.orderNumber,
            total: formatCatalogPrice(data.values.codAmount),
          })}
        </p>
        {failure !== null ? (
          <p role="alert" className="rounded-md bg-criticalSoft px-4 py-3 text-sm text-critical">
            {t('merchant.sendJibex.failed', { reason: failure })}
          </p>
        ) : null}
        {requestError !== null ? (
          <p role="alert" className="rounded-md bg-criticalSoft px-4 py-3 text-sm text-critical">
            {requestError}
          </p>
        ) : null}
        {missing.size > 0 ? (
          <p
            role="status"
            className="rounded-md border border-warn bg-warnSoft px-4 py-3 text-sm text-ink"
          >
            {t('merchant.sendJibex.missingTitle')}
          </p>
        ) : null}

        <fieldset disabled={busy} className="grid gap-4 md:grid-cols-2">
          <section className="flex flex-col gap-3 rounded-lg border border-line bg-ground p-4">
            <h3 className="font-display text-base font-semibold text-ink">
              {t('merchant.sendJibex.recipient')}
            </h3>
            <Field
              label={`${t('merchant.sendJibex.phone')} *`}
              {...whenText(fieldError('phone'), missingHint('phone'))}
            >
              {(bind) => (
                <Input
                  {...bind}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  value={values.phone}
                  onChange={(event) => {
                    set('phone', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={t('merchant.sendJibex.phone2')}
              optional
              {...whenText(fieldError('phone2'))}
            >
              {(bind) => (
                <Input
                  {...bind}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  value={values.phone2}
                  onChange={(event) => {
                    set('phone2', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.name')} *`}
              {...whenText(fieldError('recipientName'), missingHint('recipientName'))}
            >
              {(bind) => (
                <Input
                  {...bind}
                  value={values.recipientName}
                  maxLength={120}
                  onChange={(event) => {
                    set('recipientName', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.address')} *`}
              {...whenText(fieldError('addressLine'), missingHint('addressLine'))}
            >
              {(bind) => (
                <textarea
                  {...bind}
                  className="ecom-input min-h-[4.5rem]"
                  rows={2}
                  maxLength={255}
                  placeholder={t('merchant.sendJibex.addressPlaceholder')}
                  value={values.addressLine}
                  onChange={(event) => {
                    set('addressLine', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.governorate')} *`}
              {...whenText(fieldError('governorate'), missingHint('governorate'))}
            >
              {(bind) => (
                <GovernorateSelect
                  {...bind}
                  aria-label={`${t('merchant.sendJibex.governorate')} *`}
                  placeholder={t('merchant.sendJibex.select')}
                  value={values.governorate}
                  onChange={(code) => {
                    set('governorate', code);
                    set('delegation', '');
                  }}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.delegation')} *`}
              {...whenText(
                fieldError('delegation'),
                missingHint('delegation') ?? typedDelegationHint,
              )}
            >
              {(bind) => (
                <Select
                  {...bind}
                  options={delegationsOf(values.governorate).map((name) => ({
                    value: name,
                    label: name,
                  }))}
                  placeholder={t(
                    values.governorate === ''
                      ? 'merchant.sendJibex.chooseGovernorateFirst'
                      : 'merchant.sendJibex.select',
                  )}
                  disabled={values.governorate === ''}
                  value={values.delegation}
                  onChange={(event) => {
                    set('delegation', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={t('merchant.sendJibex.comment')}
              optional
              hint={t('merchant.sendJibex.commentHint')}
            >
              {(bind) => (
                <textarea
                  {...bind}
                  className="ecom-input min-h-[4.5rem]"
                  rows={2}
                  maxLength={300}
                  value={values.comment}
                  onChange={(event) => {
                    set('comment', event.target.value);
                  }}
                />
              )}
            </Field>
          </section>

          <section className="flex flex-col gap-3 rounded-lg border border-line bg-ground p-4">
            <h3 className="font-display text-base font-semibold text-ink">
              {t('merchant.sendJibex.parcel')}
            </h3>
            <Field
              label={`${t('merchant.sendJibex.price')} *`}
              hint={t('merchant.sendJibex.priceHint')}
            >
              {(bind) => (
                <Input {...bind} value={formatCatalogPrice(data.values.codAmount)} readOnly />
              )}
            </Field>
            <Field label={t('merchant.sendJibex.pieces')} {...whenText(fieldError('pieces'))}>
              {(bind) => (
                <Input
                  {...bind}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={999}
                  step={1}
                  value={values.pieces}
                  onChange={(event) => {
                    set('pieces', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.designation')} *`}
              hint={t('merchant.sendJibex.designationHint')}
            >
              {(bind) => (
                <textarea
                  {...bind}
                  className="ecom-input min-h-[4.5rem]"
                  rows={2}
                  readOnly
                  value={data.values.designation}
                />
              )}
            </Field>
            <Field
              label={`${t('merchant.sendJibex.weight')} *`}
              {...whenText(fieldError('weight'))}
            >
              {(bind) => (
                <Input
                  {...bind}
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  step="0.1"
                  value={values.weight}
                  onChange={(event) => {
                    set('weight', event.target.value);
                  }}
                />
              )}
            </Field>
            <YesNo
              legend={`${t('merchant.sendJibex.exchange')} *`}
              name="send-jibex-exchange"
              value={values.exchange}
              onChange={(value) => {
                set('exchange', value);
              }}
            />
            <Field label={`${t('merchant.sendJibex.type')} *`}>
              {(bind) => (
                <Select
                  {...bind}
                  value={values.parcelType}
                  options={PARCEL_TYPES.map((type) => ({
                    value: type,
                    // Only FIX has a name on Jibex's screen; the other codes are shown as Jibex documents them.
                    label: type === 'FIX' ? t('merchant.sendJibex.typeFIX') : type,
                  }))}
                  onChange={(event) => {
                    set('parcelType', event.target.value as SendValues['parcelType']);
                  }}
                />
              )}
            </Field>
            <YesNo
              legend={t('merchant.sendJibex.fragile')}
              name="send-jibex-fragile"
              value={values.fragile}
              onChange={(value) => {
                set('fragile', value);
              }}
            />
            <YesNo
              legend={t('merchant.sendJibex.opening')}
              name="send-jibex-opening"
              value={values.openingAllowed}
              onChange={(value) => {
                set('openingAllowed', value);
              }}
            />
          </section>
        </fieldset>
      </form>
    );
  }

  const footer =
    data !== undefined && !data.carrierConnected ? (
      <>
        <Button variant="secondary" onClick={onClose}>
          {t('merchant.common.cancel')}
        </Button>
        <Button loading={markingPacked} onClick={onMarkPacked}>
          {t('merchant.sendJibex.markPacked')}
        </Button>
      </>
    ) : showForm ? (
      <>
        <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
          {t('merchant.common.cancel')}
        </Button>
        <Button type="submit" form="send-to-jibex-form" loading={send.isPending} disabled={busy}>
          {t('merchant.sendJibex.submit')}
        </Button>
      </>
    ) : (
      <Button variant="secondary" onClick={onClose}>
        {t('common.close')}
      </Button>
    );

  return (
    <Modal
      open={orderId !== null}
      size="lg"
      title={t('merchant.sendJibex.title')}
      onClose={onClose}
      footer={footer}
    >
      {body}
    </Modal>
  );
}

/** `Field`'s optional texts, only when present (`exactOptionalPropertyTypes`). An error wins over the hint. */
function whenText(error: string | undefined, hint?: string): { error?: string; hint?: string } {
  if (error !== undefined) return { error };
  if (hint !== undefined) return { hint };
  return {};
}

/** Oui / Non side by side, as on Jibex's screen (a native radio pair: keyboard and screen readers for free). */
function YesNo({
  legend,
  name,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  value: boolean;
  onChange: (value: boolean) => void;
}): ReactElement {
  const { t } = useI18n();
  return (
    <fieldset className="flex flex-col">
      <legend className="ecom-field__label mb-1">{legend}</legend>
      <div className="flex flex-wrap gap-x-5">
        {[true, false].map((option) => (
          <label
            key={String(option)}
            className="inline-flex min-h-10 items-center gap-2 text-sm text-ink"
          >
            <input
              type="radio"
              name={name}
              className="h-4 w-4 accent-[var(--ecom-accent)]"
              checked={value === option}
              onChange={() => {
                onChange(option);
              }}
            />
            {t(option ? 'merchant.sendJibex.yes' : 'merchant.sendJibex.no')}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
